import * as Yup from 'yup';
import { useRef, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useParams, useNavigate } from 'react-router-dom';
import { useGetTableTextByKey, updateTableExplanation } from 'src/api/admin';

// MUI
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { PageContainer } from 'src/components/page-container';

// Routes & Locales
import { paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

// Components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFEditor, RHFTextField } from 'src/components/hook-form';

// Data & Types
import type { SubmitHandler } from 'react-hook-form';

type TableTextFormValues = {
  key: string;
  name: string;
  content: string;
};

export default function TableTextEditForm() {
  const { t } = useLocales('tableTextPage');
  const { enqueueSnackbar } = useSnackbar();
  const { key } = useParams();
  const navigate = useNavigate();

  const TableTextSchema = Yup.object().shape({
    key: Yup.string().required(),
    name: Yup.string().required(),
    content: Yup.string().required('Content is required'),
  });

  const methods = useForm<TableTextFormValues>({
    resolver: yupResolver(TableTextSchema),
    defaultValues: {
      key: '',
      name: '',
      content: '',
    },
  });

  const {
    reset,
    formState: { isSubmitting },
  } = methods;

  const { data: tableTextData } = useGetTableTextByKey(key);

  // This route reuses the component when :key changes, so the effect can be seeding a
  // different record than the one the user was editing. keepDirtyValues is right for a
  // revalidation of the same record — it protects the draft — but on a record switch it
  // would carry the previous record's content onto the new one and save it there. Track
  // which record was seeded and load a different one clean.
  const seededKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!tableTextData) return;
    const isSameRecord = seededKeyRef.current === tableTextData.key;
    seededKeyRef.current = tableTextData.key;

    reset(
      {
        key: tableTextData.key,
        name: tableTextData.name,
        content: tableTextData.content,
      },
      { keepDirtyValues: isSameRecord },
    );
  }, [tableTextData, reset]);

  const onSubmit: SubmitHandler<TableTextFormValues> = async (data) => {
    try {
      await updateTableExplanation({ key: data.key, content: data.content });
      enqueueSnackbar(t('saved_successfully'));
      navigate(paths.admin.dashboard.table_text);
    } catch (error) {
      enqueueSnackbar(t('save_error'), { variant: 'error' });
    }
  };

  return (
    <PageContainer variant="form">
      <CustomBreadcrumbs
        heading={t('edit')}
        links={[
          { name: t('admin') },
          { name: t('table_text'), href: paths.admin.dashboard.table_text },
          { name: t('edit') },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />
      {tableTextData?.name && (
        <Typography variant="h4" sx={{ mb: 2 }}>
          {tableTextData.name}
        </Typography>
      )}

      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">{t('table_name')}</Typography>
                  <RHFTextField
                    name="name"
                    label={t('table_name')}
                    fullWidth
                    disabled
                    slotProps={{ input: { readOnly: true } }}
                  />
                </Stack>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">{t('content')}</Typography>
                  <RHFEditor name="content" />
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="medium"
              loading={isSubmitting}
              sx={{ ml: 2, mt: 3, width: '100px' }}
            >
              {t('save')}
            </Button>
          </Grid>
        </Grid>
      </FormProvider>
    </PageContainer>
  );
}
